import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Photo } from '@capacitor/camera';
import { Directory, Filesystem, ReadFileResult, WriteFileResult } from '@capacitor/filesystem';
import { Platform } from '@ionic/angular';
import { FileType } from '../model/file.type';
import { timer } from 'rxjs';
import { finalize, retry, timeout } from 'rxjs/operators';
import { NetworkService } from './network.service';
import { SentryService } from './sentry.service';
import { normalizeFilePath } from '../utils/file-path.util';



export interface UploadFileResponse {
  data: FileData;
  message: string;
}

export interface FileData {
  name: string;
  original_name: string;
  size: number;
  extension: string;
  updated_at: Date;
  created_at: Date;
  id: number;
  link: string;
}


@Injectable({
  providedIn: 'root'
})
export class FileService {

  private static readonly UPLOAD_TIMEOUT_MS = 90_000;

  constructor(
    private readonly http: HttpClient,
    private readonly platform: Platform,
    private readonly sentryService: SentryService,
    private readonly networkService: NetworkService,
  ) { }

  uploadFile(file: Blob, retries = 2) {
    const formData = new FormData();
    const extension = file.type === 'image/png' ? 'png' : 'jpeg';
    formData.append('file', file, `photo.${extension}`);

    const connectionType = this.networkService.getConnectionType();
    const startedAt = performance.now();

    this.sentryService.addBreadcrumb({
      category: 'upload',
      message: 'Subida de archivo iniciada',
      data: { size: file.size, connectionType },
    });

    return this.http.post<UploadFileResponse>('files/upload_files', formData).pipe(
      timeout(FileService.UPLOAD_TIMEOUT_MS),
      retry({
        count: retries,
        delay: (error, retryCount) => {
          if ([413, 415, 422].includes(error?.status)) {
            throw error;
          }
          this.sentryService.addBreadcrumb({
            category: 'upload',
            message: `Reintento ${retryCount} de subida`,
            data: { size: file.size, connectionType, retryCount },
          });
          const base = 2000;
          const exp = Math.min(base * Math.pow(2, retryCount - 1), 10_000);
          const jitter = exp * (0.8 + Math.random() * 0.4);
          return timer(jitter);
        }
      }),
      finalize(() => {
        this.sentryService.addBreadcrumb({
          category: 'upload',
          message: 'Subida de archivo finalizada',
          data: { size: file.size, connectionType, durationMs: Math.round(performance.now() - startedAt) },
        });
      })
    );
  }

  downloadFile(file: string) {
    return this.http.get(`files/download_files/${file}`, { responseType: 'blob' });
  }

  async storeFileFromPath(photo: Photo, type: FileType): Promise<WriteFileResult> {
    const fileName = `${new Date().getTime()}.${photo.format}`; // Create a unique name for the file
    // const base64Data = await this.readAsBase64(photo); // Use the method to get the base64 data
    const path = `${type}/` + fileName;
    return Filesystem.writeFile({
      path,
      data: photo.base64String,
      directory: Directory.Data,
      recursive: true,
    });

  }

  async storeBase64File(base64: string, format: string, type: FileType): Promise<WriteFileResult> {
    const fileName = `${new Date().getTime()}.${format}`;
    const path = `${type}/` + fileName;
    return Filesystem.writeFile({
      path,
      data: base64,
      directory: Directory.Data,
      recursive: true,
    });
  }


  /**
   * Opciones de ruta normalizadas para Filesystem.
   * - Nativo: WriteFileResult.uri es file:///data/.../files/{type}/{name};
   *   normalizeFilePath lo deja en {type}/{name} (relativo) y hay que indicar
   *   directory: Directory.Data para resolver contra el mismo directorio donde
   *   escriben storeBase64File / storeFileFromPath.
   * - Web: los paths ya incluyen el prefijo de directorio (/DATA/...); añadir
   *   directory duplicaría el prefijo, así que se devuelven sin tocar.
   */
  private normalizeOptions(path: string): { path: string; directory?: Directory } {
    if (Capacitor.getPlatform() === 'web') {
      return { path };
    }
    return { path: normalizeFilePath(path), directory: Directory.Data };
  }

  getFileFromPath(path: string): Promise<ReadFileResult> {
    return Filesystem.readFile(this.normalizeOptions(path));
  }


  removeFileFromPath(path: string) {
    return Filesystem.deleteFile(this.normalizeOptions(path));
  }

  getFileStat(path: string) {
    return Filesystem.stat(this.normalizeOptions(path));
  }

  async fileExists(path: string): Promise<boolean> {
    try {
      await Filesystem.stat(this.normalizeOptions(path));
      return true;
    } catch (error) {
      return false;
    }
  }

}
